import { useRef, useState, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { X, Scan } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import type { PhotoCategory } from "./ClientPhotoUpload";

interface CameraOverlayProps {
  category: PhotoCategory;
  onCapture: (file: File) => void;
  onClose: () => void;
  mlDetection?: boolean;
}

const hasGuideCircle = (category: PhotoCategory) =>
  category === "hands" || category === "feet" || category === "face";

const CameraOverlay = ({ category, onCapture, onClose, mlDetection = true }: CameraOverlayProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const analyzeTimerRef = useRef<number | null>(null);
  const autoCaptureTimerRef = useRef<number | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quarterDetected, setQuarterDetected] = useState(false);
  const [handInPosition, setHandInPosition] = useState(false);
  const [autoCapturing, setAutoCapturing] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const countdownRef = useRef<number | null>(null);
  const [greenFlash, setGreenFlash] = useState(false);

  // Start camera
  useEffect(() => {
    let cancelled = false;
    const start = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: category === "face" ? "user" : "environment", width: { ideal: 1280 }, height: { ideal: 960 } },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => setReady(true);
        }
      } catch {
        setError("Camera access denied. Please allow camera permissions.");
      }
    };
    start();
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach(t => t.stop());
      if (analyzeTimerRef.current) clearInterval(analyzeTimerRef.current);
      if (autoCaptureTimerRef.current) clearTimeout(autoCaptureTimerRef.current);
    };
  }, []);

  // Capture full-resolution photo
  const capture = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `${category}-${Date.now()}.jpg`, { type: "image/jpeg" });
        onCapture(file);
      }
    }, "image/jpeg", 0.9);
  }, [category, onCapture]);

  // Capture a low-res frame for analysis
  const captureFrameBase64 = useCallback((): string | null => {
    const video = videoRef.current;
    const canvas = document.createElement("canvas");
    if (!video) return null;
    const scale = 0.25;
    canvas.width = video.videoWidth * scale;
    canvas.height = video.videoHeight * scale;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", 0.4).split(",")[1];
  }, []);

  // Analyze frame via edge function
  const analyzeFrame = useCallback(async () => {
    if (!ready) return;
    const base64 = captureFrameBase64();
    if (!base64) return;
    try {
      const { data, error: fnErr } = await supabase.functions.invoke("analyze-photo", {
        body: { image: base64, category },
      });
      if (fnErr || !data) return;

      const newQuarter = data.quarter_detected || false;
      const inPos = (data.detected && data.centered) || false;

      if (newQuarter && !quarterDetected) {
        setGreenFlash(true);
        setTimeout(() => setGreenFlash(false), 1200);
      }
      setQuarterDetected(newQuarter);
      setHandInPosition(inPos);
    } catch {
      // silently fail
    }
  }, [ready, captureFrameBase64, category, quarterDetected]);

  // Periodic ML analysis
  useEffect(() => {
    if (!mlDetection || !ready) return;
    // Initial analysis after 1.5s
    const initial = window.setTimeout(analyzeFrame, 1500);
    analyzeTimerRef.current = window.setInterval(analyzeFrame, 3000);
    return () => {
      clearTimeout(initial);
      if (analyzeTimerRef.current) clearInterval(analyzeTimerRef.current);
    };
  }, [mlDetection, ready, analyzeFrame]);

  // Auto-capture logic
  useEffect(() => {
    if (!mlDetection || autoCapturing) return;

    const shouldCapture = category === "face"
      ? handInPosition
      : handInPosition && quarterDetected;

    if (shouldCapture) {
      setAutoCapturing(true);
      setCountdown(3);

      let c = 3;
      countdownRef.current = c;
      const tick = setInterval(() => {
        c -= 1;
        countdownRef.current = c;
        setCountdown(c);
        if (c <= 0) {
          clearInterval(tick);
          capture();
        }
      }, 1000);

      autoCaptureTimerRef.current = tick as unknown as number;
    }
  }, [handInPosition, quarterDetected, category, mlDetection, autoCapturing, capture]);

  // Reset auto-capture if object exits the circle with ≤1 second remaining
  useEffect(() => {
    if (!handInPosition && autoCapturing) {
      // Always reset if ≤1s left on countdown
      const remaining = countdownRef.current;
      if (remaining !== null && remaining <= 1) {
        // Reset completely — don't let it fire
        setAutoCapturing(false);
        setCountdown(null);
        countdownRef.current = null;
        if (autoCaptureTimerRef.current) {
          clearInterval(autoCaptureTimerRef.current);
          autoCaptureTimerRef.current = null;
        }
      } else {
        // Object left with >1s remaining — also reset
        setAutoCapturing(false);
        setCountdown(null);
        countdownRef.current = null;
        if (autoCaptureTimerRef.current) {
          clearInterval(autoCaptureTimerRef.current);
          autoCaptureTimerRef.current = null;
        }
      }
    }
  }, [handInPosition, autoCapturing]);

  const showGuide = hasGuideCircle(category);
  const showQuarterPrompt = showGuide && category !== "face" && mlDetection;

  // Determine circle color: green = in position, yellow = detected but not centered, default = nothing
  const circleColor = handInPosition
    ? "border-green-400 shadow-[0_0_30px_rgba(74,222,128,0.5)]"
    : quarterDetected || (mlDetection && ready)
      ? "border-yellow-400 shadow-[0_0_20px_rgba(250,204,21,0.3)]"
      : "border-white/30";

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      {/* Close button */}
      <div className="absolute top-4 right-4 z-10">
        <Button variant="ghost" size="icon" onClick={onClose} className="text-white hover:bg-white/20">
          <X size={24} />
        </Button>
      </div>

      {error ? (
        <div className="flex-1 flex items-center justify-center p-6 text-center">
          <p className="text-white/80 text-sm">{error}</p>
        </div>
      ) : (
        <>
          <div className="flex-1 relative overflow-hidden">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="absolute inset-0 w-full h-full object-cover"
            />

            {/* Green flash border when quarter detected */}
            {greenFlash && (
              <div className="absolute inset-0 border-4 border-green-400 z-20 pointer-events-none animate-pulse" />
            )}

            {/* Guide circle overlay */}
            {showGuide && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div
                  className={`w-[70vw] max-w-[22rem] aspect-square rounded-full border-[3px] transition-all duration-500 ${circleColor}`}
                />
              </div>
            )}

            {/* Quarter prompt */}
            {showQuarterPrompt && (
              <div className="absolute bottom-4 left-0 right-0 text-center pointer-events-none z-10">
                <span className="bg-black/60 text-white/80 text-xs px-4 py-2 rounded-full tracking-wider uppercase">
                  Place a quarter anywhere in frame for scale
                </span>
              </div>
            )}

            {/* ML status indicators */}
            {mlDetection && ready && (
              <div className="absolute top-4 left-4 z-10 flex flex-col gap-2">
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] uppercase tracking-wider font-medium ${
                  handInPosition ? "bg-green-500/80 text-white" : "bg-white/20 text-white/60"
                }`}>
                  <div className={`w-2 h-2 rounded-full ${handInPosition ? "bg-white" : "bg-white/40"}`} />
                  {category === "hands" ? "Hand" : category === "feet" ? "Feet" : "Face"}
                </div>
                {showGuide && category !== "face" && (
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] uppercase tracking-wider font-medium ${
                    quarterDetected ? "bg-green-500/80 text-white" : "bg-white/20 text-white/60"
                  }`}>
                    <div className={`w-2 h-2 rounded-full ${quarterDetected ? "bg-white" : "bg-white/40"}`} />
                    Quarter
                  </div>
                )}
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] uppercase tracking-wider font-medium bg-white/10 text-white/50">
                  <Scan size={10} />
                  AI Detection
                </div>
              </div>
            )}

            {/* Auto-capture countdown */}
            {countdown !== null && countdown > 0 && (
              <div className="absolute inset-0 flex items-center justify-center z-30 pointer-events-none">
                <div className="w-24 h-24 rounded-full bg-black/40 border-2 border-green-400 flex items-center justify-center">
                  <span className="text-white text-4xl font-bold">{countdown}</span>
                </div>
              </div>
            )}
          </div>

          {/* Shutter button (always available) */}
          <div className="p-6 flex justify-center bg-black/80">
            <button
              onClick={capture}
              disabled={!ready}
              className="w-16 h-16 rounded-full border-4 border-white flex items-center justify-center disabled:opacity-40 transition-opacity active:scale-95"
            >
              <div className="w-12 h-12 rounded-full bg-white" />
            </button>
          </div>
        </>
      )}

      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
};

export default CameraOverlay;
